import React from "react";
import PropTypes from 'prop-types';
import { GoogleMap, LoadScript, Marker, MarkerClusterer, InfoWindow } from '@react-google-maps/api';
import { injectIntl } from 'react-intl';
import classNames from "classnames";

import pinImage from '../../images/navi/pin-hard.svg';
import multiPinImage from '../../images/navi/pin-hard-multi.svg';
import StationInfo from "../Station/StationInfo";
import { Icon } from "@rmwc/icon";

const MARKER_STYLES = [
    {
        url: multiPinImage,
        height: 56,
        width: 40,
        textColor: '#fff',
        textSize: 16,
        fontFamily: "Open Sans, sans-serif"
    }];

const MAP_STYLE = {
    width: '100%',
    height: '100%'
}

const MARKER_ICON = pinImage;

class Map extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            openedInfoWindow: -1,
            gettingCurrentPosition: false
        };

        this.centerMap = this.centerMap.bind(this);
    }

    clickMarker(index) {
        this.setState({
            openedInfoWindow: index === this.state.openedInfoWindow ? -1 : index
        });
    }

    onMapLoad(map) {
        this.setState({
            map: map
        }, this.centerMap);
    }

    centerMap() {
        if (!!navigator && !!navigator.geolocation) {
            this.setState({
                gettingCurrentPosition: true
            }, () => {
                navigator.geolocation.getCurrentPosition(
                    ({ coords: { latitude: lat, longitude: lng } }) => {
                        console.debug(`Received navigation coordinates: ${lat}, ${lng}`);

                        this.state.map.setCenter({ lat, lng });
                        this.state.map.setZoom(this.props.focusZoom);
                        this.setState({
                            gettingCurrentPosition: false
                        });
                    },
                    (error) => {
                        console.debug(`Could not get nav coordinates: ${error.code}, ${error.message}`);
                        this.setState({
                            gettingCurrentPosition: false
                        });
                    });
            })
        } else {
            console.debug("Navigator api not present");
        }
    }

    onInfoWindowLoad(iw) {
        setTimeout(() => {
            const parent = iw.content.parentElement;
            if (!!parent) {
                parent.style["overflow"] = "hidden";
            }
        }, 100);
    }

    render() {
        const { zoom, center, items } = this.props;
        const { gettingCurrentPosition } = this.state;

        return (
            <div className="map">
                <LoadScript
                    googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_KEY}
                >
                    <GoogleMap
                        mapContainerStyle={MAP_STYLE}
                        center={center}
                        zoom={zoom}
                        onLoad={this.onMapLoad.bind(this)}
                    >
                        <MarkerClusterer styles={MARKER_STYLES}>
                            {(clusterer) =>
                                items
                                    .map((x, index) =>
                                        <Marker key={'marker-' + x.key}
                                            position={x.coords}
                                            onClick={this.clickMarker.bind(this, index)}
                                            icon={MARKER_ICON}
                                            clusterer={clusterer}>
                                            {this.state.openedInfoWindow === index &&
                                                <InfoWindow key={'marker-info' + x.key} onCloseClick={this.clickMarker.bind(this, -1)}
                                                    onLoad={this.onInfoWindowLoad}
                                                    options={{ minWidth: "300px" }}>
                                                    <>
                                                        <StationInfo {...x} />
                                                        <i className="map-close icon icon-close"
                                                            onClick={this.clickMarker.bind(this, -1)}
                                                        ></i>
                                                    </>
                                                </InfoWindow>
                                            }
                                        </Marker>
                                    )
                            }
                        </MarkerClusterer>
                    </GoogleMap>
                </LoadScript>
                {!!this.state.map &&
                    <div className={classNames("map-focus", { "map-focus--rotating": gettingCurrentPosition })}
                        onClick={this.centerMap}>
                        <Icon icon={{
                            strategy: 'component',
                            icon: (
                                <svg viewBox="0 0 20 20"
                                    style={{ width: "20px", height: "20px" }}>
                                    <g stroke="none" fill="none" fillRule="evenodd">
                                        <g transform="translate(-300.000000, -7839.000000)" fill="#666666">
                                            <g transform="translate(56.000000, 160.000000)">
                                                <path d="M255,7689 C255,7689.552 254.552,7690 254,7690 C253.448,7690 253,7689.552 253,7689 C253,7688.448 253.448,7688 254,7688 C254.552,7688 255,7688.448 255,7689 M259,7690 L259.91,7690 C259.486,7692.507 257.507,7694.486 255,7694.91 L255,7694 C255,7693.448 254.552,7693 254,7693 C253.448,7693 253,7693.448 253,7694 L253,7694.91 C250.493,7694.486 248.514,7692.507 248.09,7690 L249,7690 C249.552,7690 250,7689.552 250,7689 C250,7688.448 249.552,7688 249,7688 L248.09,7688 C248.514,7685.493 250.493,7683.514 253,7683.09 L253,7684 C253,7684.552 253.448,7685 254,7685 C254.552,7685 255,7684.552 255,7684 L255,7683.09 C257.507,7683.514 259.486,7685.493 259.91,7688 L259,7688 C258.448,7688 258,7688.448 258,7689 C258,7689.552 258.448,7690 259,7690 M263,7688 L261.931,7688 C261.479,7684.383 258.617,7681.521 255,7681.069 L255,7680 C255,7679.448 254.552,7679 254,7679 C253.448,7679 253,7679.448 253,7680 L253,7681.069 C249.383,7681.521 246.521,7684.383 246.069,7688 L245,7688 C244.448,7688 244,7688.448 244,7689 C244,7689.552 244.448,7690 245,7690 L246.069,7690 C246.521,7693.617 249.383,7696.479 253,7696.931 L253,7698 C253,7698.552 253.448,7699 254,7699 C254.552,7699 255,7698.552 255,7698 L255,7696.931 C258.617,7696.479 261.479,7693.617 261.931,7690 L263,7690 C263.552,7690 264,7689.552 264,7689 C264,7688.448 263.552,7688 263,7688" ></path>
                                            </g>
                                        </g>
                                    </g>
                                </svg>
                            )
                        }} />
                    </div>}
            </div>
        );
    }
}
Map.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.object
    ),
    zoom: PropTypes.number,
    center: PropTypes.object,
    focusZoom: PropTypes.number,
};

Map.defaultProps = {
    items: [],
    zoom: 7,
    center: {
        lat: 58.8,
        lng: 25
    },
    focusZoom: 12,
};

export default injectIntl(Map);
